import { useMemo, useState } from 'react';
import { formatDuration, formatUKForDisplay, getCounterpartyNumber } from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import { useRecentCalls, type RecentCallsFilters } from '../calls/useRecentCalls';
import { CallReviewPanel } from '../calls/CallReviewPanel';
import { CallReviewSheet } from '../calls/CallReviewSheet';
import { CallList } from '../calls/CallList';

export function Calls(): JSX.Element {
  const [phone, setPhone] = useState('');
  const [hasRecording, setHasRecording] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filters: RecentCallsFilters = useMemo(
    () => ({
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(hasRecording ? { hasRecording: 'true' as const } : {}),
    }),
    [phone, hasRecording],
  );

  const calls = useRecentCalls(100, filters);
  const items = calls.data?.items ?? [];

  const { selectedCall, prevCall, nextCall } = useMemo(() => {
    if (!selectedId) return { selectedCall: null, prevCall: null, nextCall: null };
    const idx = items.findIndex((c) => c.id === selectedId);
    if (idx < 0) return { selectedCall: null, prevCall: null, nextCall: null };
    return {
      selectedCall: items[idx] ?? null,
      prevCall: items[idx + 1] ?? null,
      nextCall: idx > 0 ? items[idx - 1] ?? null : null,
    };
  }, [items, selectedId]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Calls</h1>
        <p className="text-sm text-ink-500">{calls.data?.total ?? 0} total</p>
      </header>

      <div className="card p-3 flex flex-wrap items-center gap-3">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Filter by phone (substring)…"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={hasRecording}
            onChange={(e) => setHasRecording(e.target.checked)}
          />
          With recording only
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
        <div className="card overflow-hidden">
          <div className="md:hidden">
            <CallList
              items={items}
              selectedId={selectedId}
              onSelect={setSelectedId}
              loading={calls.isLoading}
              error={calls.isError}
            />
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
                <tr>
                  <th className="px-4 py-2">Started</th>
                  <th className="px-4 py-2">Direction</th>
                  <th className="px-4 py-2">Our Number</th>
                  <th className="px-4 py-2">Counterparty</th>
                  <th className="px-4 py-2">Number</th>
                  <th className="px-4 py-2">Duration</th>
                  <th className="px-4 py-2">Disposition</th>
                  <th className="px-4 py-2">Rec</th>
                </tr>
              </thead>
              <tbody>
                {calls.isLoading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">Loading…</td></tr>
                )}
                {calls.isError && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-red-600">Failed to load calls.</td></tr>
                )}
                {!calls.isLoading && items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-ink-500">No calls.</td></tr>
                )}
                {items.map((c) => {
                  const isSelected = c.id === selectedId;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className={`cursor-pointer border-t border-ink-100 hover:bg-ink-50 ${isSelected ? 'bg-brand-50' : ''}`}
                    >
                      <td className="px-4 py-2 text-ink-500">{relativeTime(c.startedAt)}</td>
                      <td className="px-4 py-2 capitalize">{c.direction}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {formatUKForDisplay(c.direction === 'outbound' ? c.fromNumber : c.toNumber)}
                        {(c as any).provider === 'VOIPLINE' && (
                          <span className="ml-1.5 text-[9px] font-sans font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase tracking-wider">
                            VP
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {c.lead ? `${c.lead.firstName} ${c.lead.lastName}${c.lead.company ? ` · ${c.lead.company}` : ''}` : '–'}
                      </td>
                      <td className="px-4 py-2 font-mono">{formatUKForDisplay(getCounterpartyNumber(c))}</td>
                      <td className="px-4 py-2 font-mono">{formatDuration(c.durationSeconds ?? 0)}</td>
                      <td className="px-4 py-2">{c.disposition ?? c.status}</td>
                      <td className="px-4 py-2 text-center">{c.recordingUrl ? '●' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hidden lg:block">
          <CallReviewPanel
            selectedCall={selectedCall}
            prevCall={prevCall}
            nextCall={nextCall}
            onSelect={setSelectedId}
          />
        </div>
      </div>

      <CallReviewSheet
        selectedCall={selectedCall}
        prevCall={prevCall}
        nextCall={nextCall}
        onSelect={setSelectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
